import React, { useEffect, useState } from "react";
import { FaCalendarPlus, FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import axios from "../../api/axios";
import BottomNav from "../../components/BottomNav";
import HeaderWithBack from "../../components/HeaderWithBack";
import Loading from "../../components/Loading";
import LogCode from "../../components/LogCode";
import { useAuthStore } from "../../stores/authStores";
import { daysFromToday, formatDateId } from "../../utils/date";

const ReferenceList: React.FC = () => {
  const navigate = useNavigate();
  const [references, setReferences] = useState<any>([]);
  const authToken = useAuthStore((state) => state.token);
  const userData = useAuthStore((state) => state.user);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!authToken) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get("reference", {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        setReferences(res.data.data || []);
      } catch (error) {
        console.error("Error fetching references:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [authToken]);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "final":
        return "bg-blue-500";
      case "pending":
        return "bg-yellow-500";
      case "expired":
      case "rejected":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const isExpired = (item: any) => {
    const daysLeft = daysFromToday(item?.reference_expired_date);
    return daysLeft !== null && daysLeft < 0;
  };

  const getStatusText = (status: string) => {
    switch (status?.toLowerCase()) {
      case "final":
        return "Berlaku";
      case "expired":
        return "Kedaluwarsa";
      default:
        return status;
    }
  };

  // Vale: rujukan langsung dikirim ke PrimeCenter saat dokter submit summary, tanpa booking pasien
  const requiresBooking = (item: any) => item?.requires_booking !== false;

  const canBook = (item: any) => {
    const hasReferenceLog = (item?.teleconsult?.prime_logs?.length || 0) > 0;
    return requiresBooking(item) && !item?.is_booked && !hasReferenceLog;
  };

  const getApprovalLabel = (item: any) => {
    const log = item?.teleconsult?.prime_logs?.[0];
    if (!log || log.status === "pending") return { text: "Sedang Diproses", className: "bg-yellow-100 text-yellow-700" };
    if (log.status === "failed") return { text: "Gagal Dikirim", className: "bg-red-100 text-red-700" };
    if (log.approval_status === "approved") return { text: "Disetujui PrimeCenter", className: "bg-green-100 text-green-700" };
    if (log.approval_status === "rejected") return { text: "Ditolak PrimeCenter", className: "bg-red-100 text-red-700" };
    return { text: "Menunggu Approval PrimeCenter", className: "bg-yellow-100 text-yellow-700" };
  };

  const handleBooking = (item: any) => {
    const hasSpeciality = item.speciality_code || item.speciality_name;
    navigate(`/reference/${item.id}/booking/doctors`, {
      state: {
        providerCode: item.provider_code,
        specialityCode: item.speciality_code,
        specialityName: item.speciality_name,
        polyCode: item.poly_code,
        polyName: item.poly_name || item.poly,
        sickLeaveDate: item.sick_leave_date,
      },
    });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <HeaderWithBack title="Rujukan" />

      {/* Profile Box */}
      <div className="bg-white p-4">
        <div className="bg-gray-100 rounded-md p-3 flex items-center justify-between shadow-sm">
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-gray-300 mr-3" />
            <span className="font-medium">
              {userData ? userData?.person?.name : ""}
              <span className="text-sm text-gray-500"> (Me)</span>
            </span>
          </div>
        </div>
      </div>

      {/* List of References */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loading />
        </div>
      ) : (
        <div className="px-4 pb-20">
          {references.length === 0 ? (
            <div className="text-center text-gray-500 mt-10">
              Tidak ada rujukan
            </div>
          ) : (
            references.map((item: any, index: number) => {
              const displayStatus = isExpired(item) ? "expired" : item.status;

              return (
              <div key={index} className="mb-4">
                <div className="bg-teal-500 text-white px-3 py-1 rounded-t-md text-sm font-medium flex items-center justify-between">
                  <span>{formatDateId(item.reference_date)}</span>
                  <span
                    className={`text-xs px-2 py-1 rounded ${getStatusColor(displayStatus)}`}
                  >
                    {getStatusText(displayStatus)}
                  </span>
                </div>
                <div className="border rounded-b-md p-4 shadow-sm">
                  {item.reference_number && (
                    <p className="font-semibold">
                      No. Rujukan: {item.reference_number}
                    </p>
                  )}
                  {requiresBooking(item) ? (
                    <p className="font-medium mt-1">{item.provider_name}</p>
                  ) : (
                    item.teleconsult?.prime_logs?.[0]?.hospital_name && (
                      <p className="font-medium mt-1">
                        {item.teleconsult.prime_logs[0].hospital_name}
                      </p>
                    )
                  )}
                  <p className="text-sm text-gray-600">
                    {item.speciality_name || item.poly_name || item.poly}
                  </p>

                  <div className="text-sm mt-2 mb-3 border-l-2 border-gray-300 pl-3">
                    <p>Tipe: {item.type}</p>
                    <p>Spesialisasi: {item.speciality_name || item.poly_name || '-'}</p>
                    <p className={isExpired(item) ? "text-red-600" : undefined}>
                      Berlaku sampai: {formatDateId(item.reference_expired_date)}
                    </p>
                  </div>

                  {item.doctor_note && (
                    <div className="text-sm bg-gray-50 p-2 rounded mb-3">
                      <p className="font-medium text-gray-700">
                        Catatan Dokter:
                      </p>
                      <p className="text-gray-600">{item.doctor_note}</p>
                    </div>
                  )}

                  {item.teleconsult?.prime_logs?.some((log: any) => log.log_code) && (
                    <div className="text-sm rounded px-3 mb-3">
                      <p className="font-medium text-blue-700 mb-1">Log Code</p>
                      <div className="flex flex-wrap gap-1">
                        {item.teleconsult.prime_logs.filter((log: any) => log.log_code).map((log: any, i: number) => (
                          <span
                            key={i}
                            className="inline-block max-w-full bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded leading-snug"
                          >
                            <LogCode code={log.log_code} />
                          </span>
                        ))}
                      </div>
                      {/* Poli & dokter yang dipilih pasien saat mengajukan rujukan */}
                      {item.poly_name && (
                        <p className="text-xs text-gray-600 mt-1">
                          Poli: {item.poly_name}
                          {item.teleconsult.prime_logs[0]?.dppj &&
                            item.teleconsult.prime_logs[0].dppj !== "-" &&
                            ` · ${item.teleconsult.prime_logs[0].dppj}`}
                        </p>
                      )}
                    </div>
                  )}

                  {!requiresBooking(item) && (
                    <span
                      className={`inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3 ${getApprovalLabel(item).className}`}
                    >
                      {getApprovalLabel(item).text}
                    </span>
                  )}

                  <div className="flex items-center gap-2">
                    {canBook(item) ? (
                      <button
                        className="bg-white border border-teal-500 text-teal-600 flex items-center px-4 py-1.5 rounded-md text-sm hover:bg-teal-50 transition-colors font-medium"
                        onClick={() => handleBooking(item)}
                      >
                        <FaCalendarPlus className="mr-2" /> Booking
                      </button>
                    ) : (
                      <button
                        className="bg-teal-500 text-white flex items-center px-4 py-1.5 rounded-md text-sm hover:bg-teal-600 transition-colors"
                        onClick={() => navigate(`/reference/${item.id}`)}
                      >
                        <FaEye className="mr-2" /> Lihat Detail
                      </button>
                    )}
                  </div>
                </div>
              </div>
              );
            })
          )}
        </div>
      )}

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default ReferenceList;
